import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import { Transition, SwitchTransition } from 'react-transition-group';

import { Icon, Text } from '../../../../../components';

const AnswerTipsContainer = styled.div`
  display: flex;
  align-items: flex-start;
  column-gap: 12px;

  background-color: var(--gray-6);

  padding: 12px;
  border-radius: 8px;
  box-sizing: border-box;
  max-width: 480px;
`;

const IconContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: var(--elevation-high);

  width: 40px;
  height: 40px;
  border-radius: 50%;
  flex-shrink: 0;
`;

const StyledText = styled(Text)`
  transition: opacity 600ms linear;

  opacity: ${({ $state }) => ($state === 'entered' || $state === 'entering' ? 1 : 0)};
`;

const tips = [
  "Try to use exact spelling if you're referencing a customer or another company name. Don't worry about capitalization though.",
  'Feel free to reference exact fields in your database if you know them, but Supper will make its best guess as well.',
  'You can use exact date ranges, or general date periods. "Last quarter" or "this year" is fine. Or try "recently" and Supper will pick!',
  "If you want to group a large list, tell Supper to 'group into several buckets by size' - you don't have to be specific.",
  "It's best to start your asset creation process with a general question - you can refine it afterwards to narrow it down.",
  'Click "undo" to remove the last question or refinement, it may take a couple of tries to get exactly what you\'re looking for.',
  "Experiment with tone! Working with Supper's AI is a lot like talking to a data analyst, but it's also like talking to a database. You'll get better with practice.",
  'Supper can get things wrong! Make sure to double and triple check results.',
  'Supper is NOT great with "why" questions - but we\'re working on it.',
  'If Supper is struggling with multiple filters or groupings, try asking them in sequential questions and refining the data set a little more granularly.',
  "Sometimes Supper will break a complex request into smaller steps - you can see each step individually but you can't edit them. You need to undo that entire block to try something else.",
  'Start with "Show me" to create a view of the data you want to use to create your asset.',
  'To filter out results you don\'t want to see, use "filter by" — we will filter the the data accordingly.',
  'Group results by a column, with “group by” — this is helpful to see data grouped by the same value.',
  'Create custom categories with explicit ranges, such as "Break down by 0-10, 10-20, 20+"',
  'To sort/arrange rows of data, use "order by". When aggregating rows together, use "group by"'
];

const AnswerTips = ({ className = null }) => {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setIndex((prevIndex) => (prevIndex + 1) % tips.length);
    }, 8000);
    return () => clearInterval(interval);
  }, []);

  return (
    <AnswerTipsContainer className={className}>
      <IconContainer>
        <Icon name='lightbulb' />
      </IconContainer>
      <SwitchTransition>
        <Transition key={index} timeout={600} mountOnEnter unmountOnExit>
          {(transitionState) => <StyledText $state={transitionState}>{tips[index]}</StyledText>}
        </Transition>
      </SwitchTransition>
    </AnswerTipsContainer>
  );
};

AnswerTips.propTypes = {
  className: PropTypes.string
};

export { AnswerTips };
